import axiosLib from 'axios';
import { instance as axios, setJwtToken } from '../config/axiosInstance';

// Isolated client for OTP calls to avoid global auth-logout interceptor side effects.
const otpClient = axiosLib.create({
  baseURL: axios.defaults.baseURL,
});

/**
 * Send a download OTP to the admin's registered mobile via MSG91.
 * @param {string} phone       - Admin's 10-digit mobile number
 * @param {string} phonePrefix - Country code e.g. "91"
 * @param {string} email       - Admin's email (used for account validation)
 * @returns {Promise<{ success: boolean, message?: string }>}
 */
export const sendDownloadOtpApi = async (phone, phonePrefix, email, purpose = 'Sheet Download', activityModule = '') => {
  const { data } = await otpClient.post(
    '/auth/send-otp',
    { phone, phonePrefix, userType: 'admin', email: email || '', purpose, activityModule },
    setJwtToken()
  );
  return { success: data.success === true, message: data.message };
};

/**
 * Verify the download OTP entered by admin via MSG91.
 * @param {string} phone       - Admin's mobile number
 * @param {string} phonePrefix - Country code e.g. "91"
 * @param {string} otp         - 6-digit OTP
 * @returns {Promise<{ success: boolean, message?: string }>}
 */
export const verifyDownloadOtpApi = async (phone, phonePrefix, otp, email = '', purpose = '', activityModule = '') => {
  const { data } = await otpClient.post(
    '/auth/admin/verify-otp',
    { phone, phonePrefix, otp, email, purpose, activityModule },
    setJwtToken()
  );
  return { success: data.success === true, message: data.message };
};

/**
 * Super-admin password re-check for OTP-protected downloads/actions.
 */
export const verifyDownloadPasswordBypassApi = async (password, purpose = '', activityModule = '') => {
  const { data } = await otpClient.post(
    '/auth/admin/password-bypass',
    { password: password || '', purpose, activityModule },
    setJwtToken()
  );
  return { success: data.success === true, message: data.message };
};

/**
 * @deprecated - Use OTP-based download protection instead.
 * Kept for backward compatibility with any code still referencing it.
 */
export const verifySheetDownloadPasswordApi = async (password) => {
  const { data } = await otpClient.post(
    '/admin/verify-sheet-download-password',
    { password: password || '' },
    setJwtToken()
  );
  return { success: data.success === true, message: data.message };
};

/**
 * Set or update sheet download password (stored encrypted in backend).
 */
export const setSheetDownloadPasswordApi = async (password) => {
  const { data } = await axios.post(
    '/admin/settings/sheet-download-password',
    { password },
    setJwtToken()
  );
  return data;
};

/**
 * Check if sheet download password is configured.
 */
export const getSheetDownloadPasswordStatusApi = async () => {
  const { data } = await axios.get(
    '/admin/settings/sheet-download-password/status',
    setJwtToken()
  );
  return data?.result?.configured ?? false;
};
